/**
 * CoordinatorDashboardPage (Phase-2, Module 11)
 *
 * Coordinator landing page — pending approval queues (admissions, investigations,
 * blood tests, insurance, labs) + platform totals + recent activity. Backed by
 * `GET /api/v1/dashboard/coordinator` (CIVILHEALTH_COORDINATOR / SYSTEM_ADMIN).
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Card, CardContent, Typography, Stack, Chip, Button, Divider } from '@mui/material';
import {
  LocalHospital as AdmIcon, Biotech as InvIcon, WaterDrop as BloodIcon, Shield as InsIcon,
  Science as LabIcon, People as PeopleIcon, MedicalServices as DocIcon, Event as EventIcon,
} from '@mui/icons-material';
import { PageScaffold, KPICard } from '../../components/common';
import { RootState, AppDispatch } from '../../store';
import { fetchCoordinatorDashboard } from '../../store/slices/dashboardSlice';

const CoordinatorDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { coordinatorDashboard, loading, error } = useSelector((s: RootState) => s.dashboard);

  useEffect(() => { dispatch(fetchCoordinatorDashboard() as any); }, [dispatch]);

  const d = coordinatorDashboard;

  const queues = [
    { label: 'Pending Admissions', value: d?.pendingAdmissions ?? 0, icon: <AdmIcon />, color: '#F59E0B', path: '/admin/admissions' },
    { label: 'Pending Investigations', value: d?.pendingInvestigations ?? 0, icon: <InvIcon />, color: '#EC4899', path: '/admin/investigations' },
    { label: 'Pending Blood Tests', value: d?.pendingBloodTests ?? 0, icon: <BloodIcon />, color: '#EF4444', path: '/admin/investigations' },
    { label: 'Insurance Verifications', value: d?.pendingInsuranceVerifications ?? 0, icon: <InsIcon />, color: '#10B981', path: '/admin/insurance' },
    { label: 'Pending Lab Approvals', value: d?.pendingLabApprovals ?? 0, icon: <LabIcon />, color: '#6366F1', path: '/admin/laboratories' },
  ];

  return (
    <PageScaffold title="Coordinator Dashboard" subtitle="Approval queues & platform oversight" icon={<PeopleIcon />}
      loading={loading && !d} error={error} onDismissError={() => {}}>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}><KPICard title="Total Officers" value={d?.totalOfficers ?? 0} subtitle="Registered" icon={<PeopleIcon />} color="primary" /></Grid>
        <Grid item xs={12} sm={6} md={4}><KPICard title="Total Doctors" value={d?.totalDoctors ?? 0} subtitle="Active" icon={<DocIcon />} color="success" /></Grid>
        <Grid item xs={12} sm={6} md={4}><KPICard title="Today's Appointments" value={d?.todayAppointments ?? 0} subtitle={`${d?.pendingAppointments ?? 0} pending`} icon={<EventIcon />} color="info" /></Grid>
        <Grid item xs={12} sm={6} md={6}><KPICard title="Total Laboratories" value={d?.totalLabs ?? 0} subtitle={`${d?.activeLabs ?? 0} active`} icon={<LabIcon />} color="purple" /></Grid>
        <Grid item xs={12} sm={6} md={6}><KPICard title="Pending Appointments" value={d?.pendingAppointments ?? 0} subtitle="Awaiting action" icon={<EventIcon />} color="warning" /></Grid>
      </Grid>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Approval Queues</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {queues.map((q) => (
          <Grid item xs={12} sm={6} md={4} key={q.label}>
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${q.color}`, height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ color: q.color }}>{q.icon}</Box>
                  <Typography variant="h4" fontWeight={700}>{q.value}</Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ mt: 1 }}>{q.label}</Typography>
                <Button size="small" sx={{ textTransform: 'none', mt: 1 }} onClick={() => navigate(q.path)}>Open queue →</Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Recent Activity</Typography>
      <Card variant="outlined">
        <CardContent>
          {(d?.recentActivities || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No recent activity.</Typography>
          ) : (
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              {(d?.recentActivities || []).map((a) => (
                <Box key={a.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2">{a.description}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.actorName} · {a.actorRole} · {a.timestamp}</Typography>
                  </Box>
                  <Chip size="small" label={a.status} variant="outlined" />
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </PageScaffold>
  );
};

export default CoordinatorDashboardPage;